import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../../context/SubscriptionContext';
import useCountdownRedirect from '../../hooks/useCountdownRedirect';
import '../../styles/SubscriptionSuccess.css';

// Features that are unlocked with subscription
const UNLOCKED_FEATURES = [
    'Kalendarz treningów',
    'Zarządzanie psami',
    'Notatki z treningów',
    'Raporty i analizy',
    'Zarządzanie trenerami',
    'Zaawansowane funkcje'
];

/**
 * SubscriptionSuccess Component
 *
 * Success page shown after completing subscription checkout.
 * Features:
 * - Auto-refreshes subscription status
 * - Shows subscription details
 * - Auto-redirects to dashboard after countdown
 */
const SubscriptionSuccess = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {
        refreshSubscriptionStatus,
        subscriptionData,
        formatDate
    } = useSubscription();

    const [isRefreshing, setIsRefreshing] = useState(true);

    // Countdown redirect hook
    const { countdown } = useCountdownRedirect('/dashboard', 10, !isRefreshing);

    // Refresh subscription on mount
    useEffect(() => {
        const sessionId = new URLSearchParams(location.search).get('session_id');
        if (sessionId) {
            console.log('Checkout completed:', sessionId);
        }

        const refresh = async () => {
            setIsRefreshing(true);
            try {
                await refreshSubscriptionStatus();
            } catch (err) {
                console.error('Error refreshing subscription:', err);
            } finally {
                setIsRefreshing(false);
            }
        };

        refresh();
    }, [location, refreshSubscriptionStatus]);

    return (
        <div className="subscription-result-container">
            <div className="result-card success">
                <div className="icon">✔</div>

                <h1>Subskrypcja Aktywowana!</h1>

                <p className="message">
                    Dziękujemy za zakup. Twoje konto zostało zaktualizowane
                    i masz teraz pełny dostęp do wszystkich funkcji.
                </p>

                {isRefreshing ? (
                    <LoadingState />
                ) : (
                    <>
                        <SubscriptionDetails
                            subscriptionData={subscriptionData}
                            formatDate={formatDate}
                        />

                        <UnlockedFeatures features={UNLOCKED_FEATURES} />

                        <p className="redirect-info">
                            Przekierowanie za {countdown} sekund...
                        </p>

                        <div className="actions">
                            <button
                                className="primary-btn"
                                onClick={() => navigate('/dashboard')}
                            >
                                Przejdź do panelu
                            </button>
                            <button
                                className="secondary-btn"
                                onClick={() => navigate('/subscription/plans')}
                            >
                                Zarządzaj subskrypcją
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * Loading state while refreshing subscription
 */
const LoadingState = () => (
    <div className="loading">
        <div className="spinner"></div>
        <p>Aktualizowanie danych...</p>
    </div>
);

/**
 * Subscription details section
 */
const SubscriptionDetails = ({ subscriptionData, formatDate }) => {
    if (!subscriptionData) return null;

    return (
        <div className="details">
            <h3>Szczegóły subskrypcji</h3>
            <div className="detail-row">
                <span>Status:</span>
                <strong>
                    {subscriptionData.status === 'ACTIVE' ? 'Aktywna' : subscriptionData.status}
                </strong>
            </div>
            {subscriptionData.endDate && (
                <div className="detail-row">
                    <span>Ważna do:</span>
                    <strong>{formatDate(subscriptionData.endDate)}</strong>
                </div>
            )}
        </div>
    );
};

/**
 * Unlocked features section
 */
const UnlockedFeatures = ({ features }) => (
    <div className="features-activated">
        <h3>Odblokowane funkcje:</h3>
        <div className="features-list">
            {features.map((feature, index) => (
                <span key={index}>{feature}</span>
            ))}
        </div>
    </div>
);

export default SubscriptionSuccess;
